import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { cx } from './cx';
import { useToastPlace, type ToastPlace } from './Dialog';
import { IconButton } from './IconButton';
import type { Tone } from './Badge';

export type ToastTone = Extract<Tone, 'success' | 'warn' | 'danger' | 'info' | 'neutral'>;

export interface ToastInput {
  title: string;
  body?: string;
  tone?: ToastTone;
  /** Milliseconds on screen. Pass 0 to make it stay until dismissed. */
  duration?: number;
}

interface ToastRecord extends ToastInput {
  id: number;
}

interface ToastApi {
  toast: (input: ToastInput) => void;
  /** Shorthands, so a success message is never three different colours again. */
  success: (title: string, body?: string) => void;
  error: (title: string, body?: string) => void;
  dismiss: (id: number) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>');
  return ctx;
}

/*
 * Ink on surface with a 2px left rule in the tone. This is the one place a
 * coloured rule is allowed, because a toast is a stamp on the screen. The
 * word carries the meaning too, for print and for colour-blind readers.
 */
const toneStyles: Record<ToastTone, { rule: string; word: string; wordClass: string }> = {
  success: { rule: 'border-l-success', word: 'Done', wordClass: 'text-success' },
  warn: { rule: 'border-l-warn', word: 'Note', wordClass: 'text-warn' },
  danger: { rule: 'border-l-danger', word: 'Failed', wordClass: 'text-danger' },
  info: { rule: 'border-l-info', word: 'Info', wordClass: 'text-info' },
  neutral: { rule: 'border-l-line-strong', word: '', wordClass: 'text-ink-3' },
};

/**
 * Where the toasts sit. Bottom-left on desktop and full-width just above
 * the bottom bar on a phone. While a sheet is open on a phone, just above
 * the sheet's footer (`--ct-toast-above`, set by ToastProvider from
 * openSheets), or at the top of the screen over a sheet with no footer.
 */
export function toastRegionClass(place: ToastPlace): string {
  return cx(
    'pointer-events-none fixed inset-x-0 z-[60] flex flex-col items-stretch gap-2 p-3 sm:inset-x-auto sm:left-0 sm:items-start sm:p-4',
    // Tailwind finds classes by reading this file, so each is written out
    // whole rather than put together.
    place === null
      ? // Above the phone's bottom bar, not over it: a toast used to
        // cover the five destinations for four and a half seconds.
        'bottom-[calc(var(--ct-bar-h)+env(safe-area-inset-bottom))]'
      : // A sheet's footer is the bar at the bottom of a phone while the
        // sheet is up, and a toast sat over its buttons. From 640px a
        // dialog is a centred panel with its buttons at the right, and the
        // toast stays at the bottom left.
        cx(
          place === 'top' ? 'top-[env(safe-area-inset-top)] sm:top-auto' : 'bottom-[var(--ct-toast-above)]',
          'sm:bottom-[calc(var(--ct-bar-h)+env(safe-area-inset-bottom))]',
        ),
    'md:bottom-0',
  );
}

/**
 * One live region, in the place toastRegionClass gives it. It moves rather
 * than being replaced while a sheet is open, so a screen reader keeps the
 * one region it already knows.
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastRecord[]>([]);
  const place = useToastPlace();
  const seq = useRef(0);
  const timers = useRef(new Map<number, ReturnType<typeof setTimeout>>());

  const dismiss = useCallback((id: number) => {
    const t = timers.current.get(id);
    if (t) {
      clearTimeout(t);
      timers.current.delete(id);
    }
    setItems((prev) => prev.filter((i) => i.id !== id));
  }, []);

  const toast = useCallback(
    (input: ToastInput) => {
      const id = ++seq.current;
      setItems((prev) => [...prev, { ...input, id }]);
      const duration = input.duration ?? (input.tone === 'danger' ? 8000 : 4500);
      // An error that vanishes before it is read is an error nobody fixed.
      if (duration > 0) timers.current.set(id, setTimeout(() => dismiss(id), duration));
    },
    [dismiss],
  );

  const api = useMemo<ToastApi>(
    () => ({
      toast,
      success: (title, body) => toast({ title, body, tone: 'success' }),
      error: (title, body) => toast({ title, body, tone: 'danger' }),
      dismiss,
    }),
    [toast, dismiss],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div
        // assertive would cut across whatever the user is reading; these are
        // confirmations, not alarms.
        role="status"
        aria-live="polite"
        aria-atomic="false"
        className={toastRegionClass(place)}
        style={typeof place === 'number' ? ({ '--ct-toast-above': `${place}px` } as CSSProperties) : undefined}
      >
        {items.map((t) => (
          <Toast key={t.id} {...t} onDismiss={() => dismiss(t.id)} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function Toast({
  title,
  body,
  tone = 'neutral',
  onDismiss,
}: ToastInput & { onDismiss?: () => void }) {
  const s = toneStyles[tone];
  return (
    <div
      className={cx(
        'pointer-events-auto flex w-full items-start gap-3 border border-line border-l-2 bg-paper-card py-3 pl-3 pr-2 shadow-none sm:w-[360px]',
        s.rule,
      )}
    >
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-ink">
          {s.word ? (
            <span className={cx('record mr-2 text-2xs uppercase tracking-[0.06em]', s.wordClass)}>{s.word}</span>
          ) : null}
          {title}
        </p>
        {body ? <p className="mt-0.5 text-xs text-ink-2">{body}</p> : null}
      </div>
      {onDismiss ? <IconButton icon="close" label="Dismiss" size="sm" onClick={onDismiss} /> : null}
    </div>
  );
}
